/**
 * The Share window: who is in this space and what they may do.
 *
 * A space's members are its team's (each with their team role); this window shows your role
 * and theirs, and sends to the Spaces window to change the team: invites, roles, members. On
 * an open board (not a space) it says so.
 */
import { For, Show, type JSX } from "solid-js";
import type { Role } from "@protocol/Role";
import { useApp } from "../../app/context";

/** What each role may do here, in a few words. */
const ROLE_HINTS: Record<Role, string> = {
  viewer: "sees the board and the others",
  commenter: "sees and comments",
  editor: "changes the board",
  owner: "everything, and who is in the team",
};

/** The Share window. */
export function ShareWindow(): JSX.Element {
  const { access, wm, spaces } = useApp();
  return (
    <div class="sharewin">
      <Show when={access.state()} fallback={<p class="muted">Waiting for the core…</p>}>
        {(state) => (
          <Show
            when={!state().open}
            fallback={
              <p class="muted" id="share-open-board">
                This board is open to everyone who can reach it. To work with others with roles (viewer, commenter, editor, owner), make a space in a team in the Spaces window.
              </p>
            }
          >
            <section class="grp">
              <h3>You</h3>
              <p id="share-you" data-role={state().role ?? "none"}>
                {state().you?.name ?? "Not a member"} · <b>{state().role ?? "no role"}</b>
                <Show when={state().role}>{(r) => <span class="muted"> ({ROLE_HINTS[r()]})</span>}</Show>
              </p>
            </section>
            <section class="grp">
              <h3>Members ({state().members.length})</h3>
              <ul class="share-list">
                <For each={state().members}>
                  {(member) => (
                    <li data-member={member.id} data-member-role={member.role}>
                      <b>{member.name}</b>
                      <Show when={state().you?.id === member.id}>
                        <span class="muted small"> (you)</span>
                      </Show>
                      <span>{member.role}</span>
                    </li>
                  )}
                </For>
              </ul>
              <p class="small muted">The members are the space's team. Invite people and change roles from the team.</p>
              <span class="btnrow">
                <Show
                  when={spaces.served && !spaces.remote}
                  fallback={
                    <button type="button" class="btn" id="share-manage" onClick={() => wm.open("home")}>
                      Manage the team
                    </button>
                  }
                >
                  <a class="btn" id="share-manage" href="/">
                    Manage the team
                  </a>
                </Show>
              </span>
            </section>
          </Show>
        )}
      </Show>
    </div>
  );
}
